// src/pages/AdminVouchers.js
import { useState, useEffect } from 'react';
import { Plus, ChevronRight, RefreshCw } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { admin } from '../services/api';

export default function AdminVouchers() {
  const theme = useTheme();
  const [batches,   setBatches]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [selected,  setSelected]  = useState(null); // batch detail view
  const [codes,     setCodes]     = useState([]);
  const [codesLoading, setCodesLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genCount,  setGenCount]  = useState(10);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', website_url: '', pro_months: 12, total_codes: 0 });
  const [creating, setCreating] = useState(false);

  useEffect(() => { loadBatches(); }, []);

  async function loadBatches() {
    setLoading(true);
    const res = await admin.listBatches();
    setLoading(false);
    if (res.success) setBatches(res.data || []);
  }

  async function openBatch(batch) {
    setSelected(batch);
    setCodesLoading(true);
    const res = await admin.listCodes(batch.id);
    setCodesLoading(false);
    if (res.success) setCodes(res.data?.codes || res.data || []);
  }

  async function handleGenerate() {
    if (!selected) return;
    setGenerating(true);
    await admin.generateCodes(selected.id, parseInt(genCount));
    setGenerating(false);
    // Reload codes
    const res = await admin.listCodes(selected.id);
    if (res.success) setCodes(res.data?.codes || res.data || []);
    loadBatches();
  }

  async function handleCreate(e) {
    e.preventDefault();
    setCreating(true);
    const res = await admin.createBatch({
      name:        form.name,
      website_url: form.website_url || undefined,
      pro_months:  parseInt(form.pro_months),
      total_codes: parseInt(form.total_codes) || 0,
    });
    setCreating(false);
    if (res.success) {
      setShowCreate(false);
      setForm({ name: '', website_url: '', pro_months: 12, total_codes: 0 });
      loadBatches();
    }
  }

  const inputStyle = { backgroundColor: theme.background, borderColor: theme.border, color: theme.text };

  // Batch detail view
  if (selected) {
    const usedCodes  = codes.filter(c => c.used_by);
    const freeCodes  = codes.filter(c => !c.used_by);

    return (
      <div className="space-y-5 pb-10">
        <div className="flex items-center gap-3">
          <button onClick={() => { setSelected(null); setCodes([]); }}
            className="text-sm font-medium hover:opacity-75 transition-opacity"
            style={{ color: theme.primary }}>
            ← Batches
          </button>
          <span style={{ color: theme.textMuted }}>/</span>
          <h1 className="text-xl font-bold" style={{ color: theme.text }}>{selected.name}</h1>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Total Codes', value: selected.total_codes || codes.length },
            { label: 'Used',        value: selected.used_codes  || usedCodes.length },
            { label: 'Available',   value: freeCodes.length },
          ].map(s => (
            <div key={s.label} className="rounded-2xl p-4" style={{ backgroundColor: theme.card }}>
              <p className="text-xs font-medium uppercase tracking-wider mb-1" style={{ color: theme.textMuted }}>{s.label}</p>
              <p className="text-2xl font-bold" style={{ color: theme.text }}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Generate */}
        <div className="rounded-2xl p-5" style={{ backgroundColor: theme.card }}>
          <p className="text-sm font-semibold mb-3" style={{ color: theme.text }}>Generate Codes</p>
          <div className="flex gap-3 items-center">
            <input type="number" min="1" max="500" value={genCount}
              onChange={e => setGenCount(e.target.value)}
              className="w-24 px-3 py-2 rounded-xl border text-sm outline-none"
              style={inputStyle} />
            <button onClick={handleGenerate} disabled={generating}
              className="flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg, #00BCD4, #4CAF50)' }}>
              <RefreshCw size={14} className={generating ? 'animate-spin' : ''} />
              {generating ? 'Generating...' : `Generate ${genCount} codes`}
            </button>
          </div>
        </div>

        {/* Codes list */}
        <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: theme.card }}>
          <div className="px-5 py-4" style={{ borderBottom: `1px solid ${theme.border}` }}>
            <p className="text-sm font-semibold" style={{ color: theme.text }}>
              Codes ({codes.length})
            </p>
          </div>
          {codesLoading ? (
            <div className="flex items-center justify-center h-24">
              <div className="w-5 h-5 rounded-full border-2 border-transparent animate-spin" style={{ borderTopColor: '#00ACC1' }} />
            </div>
          ) : codes.length === 0 ? (
            <p className="text-center py-8 text-sm" style={{ color: theme.textMuted }}>No codes yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: `1px solid ${theme.border}` }}>
                    {['Code', 'Status', 'Used By', 'Used At'].map(h => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                        style={{ color: theme.textMuted }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {codes.map((c, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${theme.border}` }}>
                      <td className="px-5 py-2.5 font-mono text-xs font-bold" style={{ color: '#00ACC1' }}>{c.code}</td>
                      <td className="px-5 py-2.5">
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
                          style={{
                            backgroundColor: c.used_by ? '#dcfce7' : '#f1f5f9',
                            color: c.used_by ? '#166534' : '#64748b',
                          }}>
                          {c.used_by ? 'Used' : 'Available'}
                        </span>
                      </td>
                      <td className="px-5 py-2.5 text-xs" style={{ color: theme.textMuted }}>{c.used_by || '—'}</td>
                      <td className="px-5 py-2.5 text-xs" style={{ color: theme.textMuted }}>
                        {c.used_at ? new Date(c.used_at).toLocaleDateString('en-ZA') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Batch list view
  return (
    <div className="space-y-5 pb-10">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold" style={{ color: theme.text }}>Voucher Batches</h1>
        <button onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white"
          style={{ background: 'linear-gradient(135deg, #00BCD4, #4CAF50)' }}>
          <Plus size={15} />New Batch
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="rounded-2xl p-5" style={{ backgroundColor: theme.card }}>
          <p className="text-sm font-semibold mb-4" style={{ color: theme.text }}>New Batch</p>
          <form onSubmit={handleCreate} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: theme.textMuted }}>Name *</label>
                <input required value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
                  placeholder="e.g. Toyota Menlyn" className="w-full px-3 py-2 rounded-xl border text-sm outline-none"
                  style={inputStyle} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: theme.textMuted }}>Website URL</label>
                <input value={form.website_url} onChange={e => setForm(f => ({...f, website_url: e.target.value}))}
                  placeholder="https://..." className="w-full px-3 py-2 rounded-xl border text-sm outline-none"
                  style={inputStyle} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: theme.textMuted }}>Pro Months</label>
                <input type="number" min="1" value={form.pro_months}
                  onChange={e => setForm(f => ({...f, pro_months: e.target.value}))}
                  className="w-full px-3 py-2 rounded-xl border text-sm outline-none"
                  style={inputStyle} />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: theme.textMuted }}>
                  Total Codes <span style={{ color: theme.textMuted }}>(auto-generate)</span>
                </label>
                <input type="number" min="0" max="100000" value={form.total_codes}
                  onChange={e => setForm(f => ({...f, total_codes: e.target.value}))}
                  placeholder="e.g. 50000"
                  className="w-full px-3 py-2 rounded-xl border text-sm outline-none"
                  style={inputStyle} />
              </div>
            </div>
            <div className="flex gap-2">
              <button type="submit" disabled={creating}
                className="px-5 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg, #00BCD4, #4CAF50)' }}>
                {creating ? 'Creating...' : form.total_codes > 0 ? `Create & generate ${Number(form.total_codes).toLocaleString()} codes` : 'Create'}
              </button>
              <button type="button" onClick={() => setShowCreate(false)}
                className="px-5 py-2 rounded-xl text-sm font-medium"
                style={{ backgroundColor: theme.border, color: theme.textSecondary }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Batch list */}
      <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: theme.card }}>
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-6 h-6 rounded-full border-2 border-transparent animate-spin" style={{ borderTopColor: '#00ACC1' }} />
          </div>
        ) : batches.length === 0 ? (
          <p className="text-center py-12 text-sm" style={{ color: theme.textMuted }}>No batches yet</p>
        ) : (
          batches.map((b, i) => (
            <button key={b.id} onClick={() => openBatch(b)}
              className="w-full flex items-center justify-between px-5 py-4 text-left hover:opacity-80 transition-opacity"
              style={{ borderBottom: i < batches.length - 1 ? `1px solid ${theme.border}` : 'none' }}>
              <div>
                <p className="text-sm font-semibold" style={{ color: theme.text }}>{b.name}</p>
                <p className="text-xs mt-0.5" style={{ color: theme.textMuted }}>
                  {b.total_codes || 0} codes · {b.used_codes || 0} used · {b.pro_months}mo Pro
                </p>
              </div>
              <ChevronRight size={16} style={{ color: theme.textMuted }} />
            </button>
          ))
        )}
      </div>
    </div>
  );
}